import { cellComponents } from "@portal/BaseComponents/Table/Components/cellComponents";
import { headerComponents } from "@portal/BaseComponents/Table/Components/headerComponents";
import { TableWrapper } from "@portal/BaseComponents/Table/Components/style";
import { defaultColumnDefs } from "@portal/BaseComponents/Table/default";
import { Formats } from "@portal/hooks/use-formats";
import { useCustomFieldColumns } from "@portal/hooks/useCustomFieldColumns";
import { useGetCustomFieldsBasedOnType } from "@portal/queries/custom-field-query";
import { useInvoiceListQuery } from "@portal/queries/invoice-query";
import { invoiceFiltersAtom } from "@portal/store/Filters/Invoices/invoice-filter";
import { customFieldAtom } from "@portal/store/Filters/customFields/custom-field";
import { tableAtom } from "@portal/store/table/table";
import { Invoice, InvoiceStatusesConfig } from "@portal/types/entities/invoice";
import { Sorter, Table } from "@sinecycle/growcomponents";
import produce from "immer";
import { useAtom, useAtomValue } from "jotai";
import { InvoiceActions } from "./Columns/Actions/InvoiceActions";
import { useColumns } from "./Columns/useColumns";
import { useQuickOperations } from "./useQuickOperations";

export type InvoiceActionPrivileges = {
  dispute: boolean;
  ptp: boolean;
  customField: boolean;
};
interface InvoiceListProps {
  actionPrivileges?: InvoiceActionPrivileges;
  invoiceStatusConfig: InvoiceStatusesConfig;
  extra: Formats;
}

function InvoiceList(props: InvoiceListProps) {
  const [tableValue, setTable] = useAtom(tableAtom);
  const [invoiceFilters, setFilters] = useAtom(invoiceFiltersAtom);
  const customFieldValue = useAtomValue(customFieldAtom);
  const filters = produce(invoiceFilters.filterValues, (draft) => {
    draft.statuses = draft.statuses ?? props.invoiceStatusConfig.ALL;
    draft.custom_fields = customFieldValue.filterValues.INVOICES.length
      ? customFieldValue.filterValues.INVOICES
      : undefined;
  });

  const { defaultColConfigs } = useColumns({
    privileges: props.actionPrivileges,
    invoiceStatusConfig: props.invoiceStatusConfig,
    extra: props.extra,
  });
  const { handleErrorCallBack, handleSuccessCallback, columns, columnLoading } =
    useCustomFieldColumns(defaultColConfigs, "INVOICE_PORTAL");
  const { isLoading: cFLoading } = useGetCustomFieldsBasedOnType(
    'INVOICE',
    handleSuccessCallback,
    Boolean(props.actionPrivileges?.customField),
    handleErrorCallBack
  );
  const { data, isLoading } = useInvoiceListQuery({
    page: tableValue.currentPage,
    size: 10,
    filters: { ...filters, ...invoiceFilters.sorting },
  });

  const loading = isLoading || cFLoading || columnLoading;

  function handleSort(sortParams: Sorter) {
    setFilters((filter) => {
      return produce(filter, (draft) => {
        draft.sorting = {
          order: sortParams.sortOrder,
          sort_col: sortParams.sortCol,
        };
      });
    });
  }
  const { items } = useQuickOperations(props.actionPrivileges);

  return (
    <TableWrapper>
      <Table<Invoice>
        loading={loading}
        gridProps={{
          rowData: data?.list,
          columnDefs: columns,
          defaultColDef: {
            ...defaultColumnDefs,
            autoHeight: false,
            sortable: false,
          },
          animateRows: true,
          suppressMovableColumns: true,
          components: { ...cellComponents, ...headerComponents },
          unSortIcon: true,
          enableCellTextSelection: true,
        }}
        tableState={tableValue}
        handlePageChange={(page) => {
          setTable((value) => {
            return produce(value, (draft) => {
              draft.currentPage = page;
              return draft;
            });
          });
        }}
        sortMode={{
          type: "remote",
          onSortChange: handleSort,
        }}
        enableColumnCustom={{
          type: "header-only",
          content: {
            preserve: {
              type: "LOCAL",
              storageKey: "INVOICE_PORTAL",
            },
          },
        }}
        quickOperations={{
          items,
          component: (
            <InvoiceActions
              privileges={props.actionPrivileges}
              invoiceStatusConfig={props.invoiceStatusConfig}
            />
          ),
        }}
        quickOperationsColumnProps={{
          cellClassRules: {
            "actions-hide": (params) =>
              props.invoiceStatusConfig.CLOSED.includes(
                params.data?.status as any
              ) ||
              invoiceFilters.ui_filters.view_status === "CLOSED" ||
              (!props.actionPrivileges?.dispute &&
                !props.actionPrivileges?.ptp),
          },
        }}
      />
    </TableWrapper>
  );
}

export { InvoiceList };
